import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { IpcButton, IpcModal, IpcTable } from '../components/ui';
import { nodeAutoCompleteRuleApi } from '../api/services';
import type { NodeAutoCompleteRuleVersionVO } from '../api/types/nodeAutoCompleteRule';
import './NodeAutoCompleteRuleVersionModal.css';

type Props = {
  open: boolean;
  onClose: () => void;
  ruleId: number;
  ruleName: string;
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];

import { formatServerDateTime, pickSnakeCamel as pick } from '../utils/formatServerDateTime';

type DetailField = { label: string; value: string; multiline?: boolean };

function buildVersionDetailFields(row: NodeAutoCompleteRuleVersionVO): DetailField[] {
  const enabled = pick(row.enabled as number | boolean | undefined, undefined);
  const enabledText = enabled === true || enabled === 1 ? '1' : enabled === false || enabled === 0 ? '0' : String(enabled ?? '—');
  return [
    { label: 'id', value: String(row.id) },
    { label: 'rule_id', value: String(pick(row.rule_id, row.ruleId) ?? '—') },
    { label: 'version', value: String(row.version) },
    { label: 'name', value: row.name || '—' },
    { label: 'enabled', value: enabledText },
    { label: 'trigger_type', value: pick(row.trigger_type, row.triggerType) || '—' },
    { label: 'trigger_key', value: pick(row.trigger_key, row.triggerKey) || '—' },
    { label: 'mq_vhost', value: pick(row.mq_vhost, row.mqVhost) || '—' },
    { label: 'mq_queue', value: pick(row.mq_queue, row.mqQueue) || '—' },
    {
      label: 'prepare_timeout_sec',
      value: String(pick(row.prepare_timeout_sec, row.prepareTimeoutSec) ?? '—'),
    },
    {
      label: 'prepare_retry_count',
      value: String(pick(row.prepare_retry_count, row.prepareRetryCount) ?? '—'),
    },
    {
      label: 'prepare_script',
      value: pick(row.prepare_script, row.prepareScript) || '—',
      multiline: true,
    },
    {
      label: 'decision_timeout_sec',
      value: String(pick(row.decision_timeout_sec, row.decisionTimeoutSec) ?? '—'),
    },
    {
      label: 'decision_retry_count',
      value: String(pick(row.decision_retry_count, row.decisionRetryCount) ?? '—'),
    },
    {
      label: 'decision_script',
      value: pick(row.decision_script, row.decisionScript) || '—',
      multiline: true,
    },
    { label: 'create_time', value: formatServerDateTime(pick(row.create_time, row.createTime)) },
  ];
}

const NodeAutoCompleteRuleVersionModal: FC<Props> = ({ open, onClose, ruleId, ruleName }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<NodeAutoCompleteRuleVersionVO[]>([]);
  const [detail, setDetail] = useState<NodeAutoCompleteRuleVersionVO | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadVersions = useCallback(async () => {
    if (!ruleId) return;
    setLoading(true);
    try {
      const res = await nodeAutoCompleteRuleApi.getVersions(ruleId, { pageNum: page, pageSize });
      setList(res.list ?? []);
      setTotal(res.total ?? 0);
    } catch (e) {
      console.error(e);
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [ruleId, page, pageSize]);

  useEffect(() => {
    if (!open) return;
    setPage(1);
    setDetail(null);
  }, [open, ruleId]);

  useEffect(() => {
    if (!open) return;
    void loadVersions();
  }, [open, loadVersions]);

  const openDetail = async (row: NodeAutoCompleteRuleVersionVO) => {
    setDetailLoading(true);
    try {
      const full = await nodeAutoCompleteRuleApi.getVersionById(ruleId, row.id);
      setDetail(full);
    } catch (e) {
      console.error(e);
      setDetail(row);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = useMemo<ColumnsType<NodeAutoCompleteRuleVersionVO>>(() => [
    {
      title: '版本号',
      key: 'version',
      width: 90,
      render: (_, row) => row.version,
    },
    {
      title: '规则名称',
      key: 'name',
      width: 180,
      ellipsis: true,
      render: (_, row) => row.name || '—',
    },
    {
      title: '触发方式',
      key: 'trigger_type',
      width: 100,
      render: (_, row) => pick(row.trigger_type, row.triggerType) || '—',
    },
    {
      title: '归档时间',
      key: 'create_time',
      width: 170,
      render: (_, row) => formatServerDateTime(pick(row.create_time, row.createTime)),
    },
  ], []);

  const detailFields = detail ? buildVersionDetailFields(detail) : [];

  return (
    <>
      <IpcModal
        open={open}
        title={`规则版本${ruleName.trim() ? ` · ${ruleName.trim()}` : ''}`}
        width={820}
        onCancel={onClose}
        className="acr-rule-version-modal"
        footer={(
          <div className="acr-rule-version-modal__footer">
            <IpcButton variant="primary" onClick={onClose}>关闭</IpcButton>
          </div>
        )}
      >
        <div className="acr-rule-version-modal__body">
          <IpcTable<NodeAutoCompleteRuleVersionVO>
            frameless
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={list}
            emptyTitle="暂无历史版本"
            emptyDescription="规则内容被执行过后再修改，才会归档历史版本"
            scroll={{ x: 600 }}
            onRow={row => ({
              onClick: () => { void openDetail(row); },
              className: 'acr-rule-version-modal__row',
            })}
            pagination={{
              current: page,
              pageSize,
              total,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              onChange: (nextPage, nextSize) => {
                setPage(nextPage);
                setPageSize(nextSize);
              },
            }}
          />
        </div>
      </IpcModal>

      <IpcModal
        open={Boolean(detail)}
        title={detail ? `版本明细 · v${detail.version}` : '版本明细'}
        width={760}
        onCancel={() => setDetail(null)}
        className="acr-rule-version-detail-modal"
        footer={(
          <div className="acr-rule-version-modal__footer">
            <IpcButton variant="primary" onClick={() => setDetail(null)}>关闭</IpcButton>
          </div>
        )}
      >
        {detailLoading ? (
          <div className="acr-rule-version-detail__loading">加载中…</div>
        ) : (
          <dl className="acr-rule-version-detail">
            {detailFields.map(field => (
              <div key={field.label} className="acr-rule-version-detail__row">
                <dt>{field.label}</dt>
                <dd>
                  {field.multiline ? (
                    <pre className="acr-rule-version-detail__pre">{field.value}</pre>
                  ) : (
                    field.value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </IpcModal>
    </>
  );
};

export default NodeAutoCompleteRuleVersionModal;
